import React, { useEffect, useState } from 'react';
import { PlusCircle, TrendingUp, TrendingDown, Wallet, Calendar, Filter, Menu, LogOut, Trash2, ArrowLeftRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { query, collection, orderBy, getDocs, where, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { signOut, getAuth } from "firebase/auth";

const Dashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('This Month');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(20);

  // const isLogin = localStorage.getItem('isLoggedIn');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const addExpense = () => {
    navigate('/add');
  };

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem('isLoggedIn');
    localStorage.clear(); // Clear all stored data
    navigate('/login');
  };

  const handleExchangeCurrency = () => { 
    navigate('/exchange');
  };

  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    balance: 0,
    transactions: 0
  });
  const [categories, setCategories] = useState([]);
  const [filterType, setFilterType] = useState('');
  // const [filterCategory, setFilterCategory] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = latest, 'asc' = oldest
  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const auth = getAuth();
  // const user = auth.currentUser;

  const handleDeleteTransaction = async (transactionId) => {
    setIsDeleting(true);
    try {
      // Delete from Firebase
      await deleteDoc(doc(db, 'expenses', transactionId));

      // Update local state
      const updatedTransactions = transactions.filter(t => t.id !== transactionId);
      setTransactions(updatedTransactions);

      // Recalculate summary
      const income = updatedTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
      const expenses = updatedTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Math.abs(t.amount), 0);
      const balance = income - expenses;

      setSummary({
        totalIncome: income,
        totalExpenses: expenses,
        balance,
        transactions: updatedTransactions.length
      });

      // Recalculate categories
      const catMap = {};
      updatedTransactions.forEach(t => {
        if (t.type === 'expense') {
          if (!catMap[t.category]) catMap[t.category] = 0;
          catMap[t.category] += Math.abs(t.amount);
        }
      });

      const catArr = Object.entries(catMap).map(([name, amount]) => ({
        name,
        amount,
        color: '#' + Math.floor(Math.random() * 16777215).toString(16),
      }));

      const totalCat = catArr.reduce((sum, c) => sum + c.amount, 0);
      catArr.forEach(cat => cat.percentage = Math.round((cat.amount / totalCat) * 100));

      setCategories(catArr);

      setShowDeleteConfirm(null);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error deleting transaction:', error);
      }
      alert('Failed to delete transaction. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    const email = localStorage.getItem("email");
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!isLoggedIn || isLoggedIn === 'false' || !email) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const q = query(
          collection(db, 'expenses'),
          where('userEmail', '==', email),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);

        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setTransactions(data);

        // Calculate summary
        const income = data.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
        const expenses = data.filter(t => t.type === 'expense').reduce((sum, t) => sum + Math.abs(t.amount), 0);
        const balance = income - expenses;

        setSummary({
          totalIncome: income,
          totalExpenses: expenses,
          balance,
          transactions: data.length
        });

        // Group by category
        const catMap = {};
        data.forEach(t => {
          if (t.type === 'expense') {
            if (!catMap[t.category]) catMap[t.category] = 0;
            catMap[t.category] += Math.abs(t.amount);
          }
        });

        const catArr = Object.entries(catMap).map(([name, amount]) => ({
          name,
          amount,
          color: '#' + Math.floor(Math.random() * 16777215).toString(16),
        }));

        const totalCat = catArr.reduce((sum, c) => sum + c.amount, 0);
        catArr.forEach(cat => cat.percentage = Math.round((cat.amount / totalCat) * 100));

        setCategories(catArr);

      } catch (err) {
        console.error('Error fetching data from Firebase:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);


  const autoBudget = summary.totalIncome * 0.7;
  const budgetUsed = summary.totalExpenses;
  const remaining = autoBudget - budgetUsed;
  const percentageUsed = autoBudget > 0 ? Math.min(100, Math.round((budgetUsed / autoBudget) * 100)) : 0;
  const filteredTransactions = transactions
    .filter(t => t.description.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(t => !filterType || t.type === filterType)
    // .filter(t => !filterCategory || t.category === filterCategory)
    .sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.createdAt?.seconds - b.createdAt?.seconds;
      } else {
        return b.createdAt?.seconds - a.createdAt?.seconds;
      }
    });

  const totalTransactions = filteredTransactions.length;
  const totalPages = Math.ceil(totalTransactions / transactionsPerPage);
  const startIndex = (currentPage - 1) * transactionsPerPage;
  const endIndex = startIndex + transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, sortOrder]);

  return (
    <>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', flexDirection: 'column' }}>
          <div className="spinner" style={{
            border: '6px solid blue',
            borderTop: '6px solid white',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite',
          }}></div>
          <p>Loading...</p>
          <style>
            {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
          </style>
        </div>
      ) : (
        <>
          <style>
            {`
            @media (max-width: 768px) {
              .mobile-header {
                flex-direction: column !important;
                gap: 15px !important;
              }
              
              .mobile-header-top {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
              }
              
              .mobile-controls {
                display: flex;
                gap: 10px;
                width: 100%;
                justify-content: space-between;
                flex-wrap: wrap;
              }
              
              .mobile-controls select {
                flex: 1;
                min-width: 120px;
              }
              
              .mobile-controls .button-group {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
              }
              
              .mobile-controls button {
                padding: 10px 15px !important;
                font-size: 12px !important;
              }
              
              .logout-btn-mobile {
                width: 100% !important;
                margin-top: 10px !important;
                justify-content: center !important;
              }
              
              .summary-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 15px !important;
              }
              
              .main-content {
                grid-template-columns: 1fr !important;
                gap: 20px !important;
              }
              
              .transaction-header {
                flex-direction: column !important;
                gap: 15px !important;
                align-items: flex-start !important;
              }
              
              .transaction-search {
                flex-direction: row !important;
                gap: 10px !important;
                width: 100% !important;
                flex-wrap: wrap;
              }
              
              .transaction-search input {
                flex: 1 !important;
                min-width: 150px !important;
              }
              
              .transaction-search button {
                flex-shrink: 0 !important;
              }
              
              .transaction-item {
                flex-direction: column !important;
                align-items: flex-start !important;
                gap: 10px !important;
                padding: 12px 0 !important;
              }
              
              .transaction-details {
                width: 100%;
              }
              
              .transaction-amount {
                align-self: flex-end;
                width: 100%;
                justify-content: space-between;
              }
              
              .category-item {
                margin-bottom: 15px !important;
              }
              
              .category-header {
                flex-wrap: wrap !important;
                gap: 5px !important;
              }
              
              .budget-section {
                margin-top: 20px !important;
              }
            }
            
            @media (max-width: 480px) {
              .dashboard-container {
                padding: 10px !important;
              }
              
              .summary-grid {
                grid-template-columns: 1fr !important;
              }
              
              .summary-card {
                padding: 20px !important;
              }
              
              .card {
                padding: 20px !important;
              }
              
              .transaction-avatar {
                width: 35px !important;
                height: 35px !important;
                font-size: 14px !important;
              }
              
              .mobile-controls button {
                padding: 8px 12px !important;
                font-size: 11px !important;
              }
              
              .mobile-controls .button-group {
                width: 100%;
                justify-content: space-between;
              }
            }
            
            @media (min-width: 1200px) {
              .summary-grid {
                grid-template-columns: repeat(4, 1fr) !important;
              }
            }

            .logout-btn:hover {
              background: rgba(239, 68, 68, 0.1) !important;
              transform: translateY(-1px);
              box-shadow: 0 6px 20px rgba(239, 68, 68, 0.2) !important;
            }
            
            .exchange-btn:hover {
              background: rgba(149, 247, 93, 0.1) !important;
              transform: translateY(-1px);
              box-shadow: 0 6px 20px rgba(108, 239, 68, 0.2) !important;
            }

            .add-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 25px rgba(102, 126, 234, 0.3) !important;
            }

            .delete-confirm-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.5);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000;
            }

            .delete-confirm-modal {
              background: white;
              border-radius: 15px;
              padding: 30px;
              max-width: 400px;
              width: 90%;
              box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
            }

            .delete-btn:hover {
              background: rgba(239, 68, 68, 0.1) !important;
              color: #EF4444 !important;
            }

            .pagination-controls {
              flex-direction: column !important;
              gap: 15px !important;
            }

            .pagination-info {
              text-align: center !important;
              order: 2;
            }

            .pagination-buttons {
              justify-content: center !important;
              flex-wrap: wrap !important;
            }

            @media (max-width: 480px) {
              .pagination-buttons button {
                padding: 6px 10px !important;
                font-size: 12px !important;
                min-width: 32px !important;
              }
              
              .pagination-buttons > div {
                gap: 2px !important;
              }
            }
          `}
          </style>

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="delete-confirm-overlay" onClick={() => setShowDeleteConfirm(null)}>
              <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: '#FEF2F2',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 15px auto'
                  }}>
                    <Trash2 size={24} color="#EF4444" />
                  </div>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
                    Delete Transaction
                  </h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                    Are you sure you want to delete this transaction? This action cannot be undone.
                  </p>
                </div>

                <div style={{
                  background: '#F9FAFB',
                  padding: '15px',
                  borderRadius: '10px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '35px',
                      height: '35px',
                      borderRadius: '8px',
                      background: showDeleteConfirm.type === 'income' ? '#10B981' : '#EF4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      {showDeleteConfirm.type === 'income' ? '+' : '-'}
                    </div>
                    <div>
                      <p style={{ margin: '0 0 2px 0', fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        {showDeleteConfirm.description}
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                        {showDeleteConfirm.category} • ${Math.abs(showDeleteConfirm.amount).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    disabled={isDeleting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '10px',
                      background: 'white',
                      color: '#666',
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      fontWeight: '500',
                      opacity: isDeleting ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteTransaction(showDeleteConfirm.id)}
                    disabled={isDeleting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: 'none',
                      borderRadius: '10px',
                      background: '#EF4444',
                      color: 'white',
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      fontWeight: '500',
                      opacity: isDeleting ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {isDeleting ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <style>
                          {`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                        `}
                        </style>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        Delete
                      </>
                    )}

                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="dashboard-container" style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            padding: '20px'
          }}>
            {/* Header */}
            <header className="mobile-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '30px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              padding: '20px 30px',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div className="mobile-header-top">
                <div>
                  <h1 style={{
                    color: 'white',
                    fontSize: '28px',
                    fontWeight: '700',
                    margin: '0 0 5px 0'
                  }}>
                    Expense Tracker
                  </h1>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    margin: 0,
                    fontSize: '16px'
                  }}>
                    Track your spending and manage your budget
                  </p>
                </div>

                <button
                  className="mobile-menu-btn"
                  style={{
                    display: 'none',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '10px',
                    color: 'white',
                    padding: '10px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                  <Menu size={20} />
                </button>
              </div>

              <div className="mobile-controls" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '10px',
                    color: 'white',
                    padding: '10px 15px',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                >
                  <option value="This Week">This Week</option>
                  <option value="This Month">This Month</option>
                  <option value="This Year">This Year</option>
                </select>

                <div className="button-group" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    className="add-btn"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      color: 'white',
                      padding: '12px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => { addExpense() }}
                  >
                    <PlusCircle size={18} />
                    <span className="btn-text">Add Transaction</span>
                  </button>

                  <button
                    className="exchange-btn"
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(52, 243, 113, 0.3)',
                      borderRadius: '12px',
                      color: '#fff',
                      padding: '12px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap',
                      backdropFilter: 'blur(10px)'
                    }}
                    onClick={handleExchangeCurrency}
                  >
                    <ArrowLeftRight size={18} />
                    <span className="btn-text">Exchange Currency</span>
                  </button>

                  <button
                    className="logout-btn"
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '12px',
                      color: '#fff',
                      padding: '12px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap',
                      backdropFilter: 'blur(10px)'
                    }}
                    onClick={handleLogout}
                  >
                    <LogOut size={18} />
                    <span className="btn-text">Logout</span>
                  </button>
                </div>
              </div>
            </header>

            {/* Summary Cards */}
            <div className="summary-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div className="summary-card" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '20px',
                padding: '25px',
                color: 'white',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', opacity: 0.9 }}>Total Balance</h3>
                  <Wallet size={20} style={{ opacity: 0.8 }} />
                </div>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>
                  ${summary.balance.toLocaleString()}
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
                  +12.5% from last month
                </p>
              </div>

              <div className="summary-card" style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '20px',
                padding: '25px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#666' }}>Total Income</h3>
                  <TrendingUp size={20} style={{ color: '#10B981' }} />
                </div>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: '700', color: '#10B981' }}>
                  ${summary.totalIncome.toLocaleString()}
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                  +8.2% from last month
                </p>
              </div>

              <div className="summary-card" style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '20px',
                padding: '25px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#666' }}>Total Expenses</h3>
                  <TrendingDown size={20} style={{ color: '#EF4444' }} />
                </div>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: '700', color: '#EF4444' }}>
                  ${summary.totalExpenses.toLocaleString()}
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                  -3.1% from last month
                </p>
              </div>

              <div className="summary-card" style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '20px',
                padding: '25px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#666' }}>Transactions</h3>
                  <Calendar size={20} style={{ color: '#8B5CF6' }} />
                </div>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: '700', color: '#333' }}>
                  {summary.transactions}
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                  This month
                </p>
              </div>
            </div>

            {/* Main Content */}
            <div className="main-content" style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '30px'
            }}>
              {/* Recent Transactions */}
              <div className="card" style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '20px',
                padding: '30px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <div className="transaction-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#333' }}>
                    Recent Transactions
                  </h2>
                  <div className="transaction-search" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '10px 15px',
                        outline: 'none',
                        fontSize: '14px',
                        background: '#F9FAFB',
                        minWidth: '200px',
                        transition: 'all 0.2s ease'
                      }}
                    />
                    <div className="filter-dropdown-container" style={{ position: 'relative' }}>
                      <button
                        className="filter-btn"
                        onClick={() => setShowFilterOptions(prev => !prev)}
                        style={{
                          border: '1px solid #E5E7EB',
                          borderRadius: '12px',
                          background: showFilterOptions ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#F9FAFB',
                          color: showFilterOptions ? 'white' : '#666',
                          padding: '10px 15px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'all 0.3s ease',
                          boxShadow: showFilterOptions ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none'
                        }}
                      >
                        <Filter size={16} />
                        <span className="btn-text">Filter</span>
                      </button>

                      {showFilterOptions && (
                        <>
                          <div
                            className="filter-dropdown-overlay"
                            onClick={() => setShowFilterOptions(false)}
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 999
                            }}
                          />
                          <div className="filter-dropdown" style={{
                            position: 'absolute',
                            top: '50px',
                            right: 0,
                            zIndex: 1000,
                            background: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(20px)',
                            padding: '20px',
                            borderRadius: '16px',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            width: '280px',
                            animation: 'slideDown 0.2s ease-out'
                          }}>
                            <div style={{ marginBottom: '20px' }}>
                              <label style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                marginBottom: '10px',
                                display: 'block',
                                color: '#374151'
                              }}>
                                Transaction Type
                              </label>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {['', 'income', 'expense'].map((type) => (
                                  <button
                                    key={type}
                                    onClick={() => setFilterType(type)}
                                    style={{
                                      flex: 1,
                                      padding: '10px 12px',
                                      border: '1px solid #E5E7EB',
                                      borderRadius: '10px',
                                      background: filterType === type ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                      color: filterType === type ? 'white' : '#666',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    {type === '' ? 'All' : type === 'income' ? 'Income' : 'Expense'}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                              <label style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                marginBottom: '10px',
                                display: 'block',
                                color: '#374151'
                              }}>
                                Sort by Date
                              </label>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {[
                                  { value: 'desc', label: 'Latest First' },
                                  { value: 'asc', label: 'Oldest First' }
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    onClick={() => setSortOrder(option.value)}
                                    style={{
                                      flex: 1,
                                      padding: '10px 12px',
                                      border: '1px solid #E5E7EB',
                                      borderRadius: '10px',
                                      background: sortOrder === option.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                      color: sortOrder === option.value ? 'white' : '#666',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* <div style={{ 
                              borderTop: '1px solid #F3F4F6', 
                              paddingTop: '15px',
                              display: 'flex',
                              gap: '10px'
                            }}>
                              <button
                                onClick={() => {
                                  setFilterType('');
                                  setFilterCategory('');
                                  setSortOrder('desc');
                                  setSearchTerm('');
                                }}
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  background: 'white',
                                  color: '#666',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}
                              >
                                Clear All
                              </button>
                              <button
                                onClick={() => setShowFilterOptions(false)}
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  border: 'none',
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}
                              >
                                Apply
                              </button>
                            </div> */}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {currentTransactions.map((transaction) => (
                    <div key={transaction.id} className="transaction-item" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '15px 0',
                      borderBottom: '1px solid #F3F4F6'
                    }}>
                      <div className="transaction-details" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="transaction-avatar" style={{
                          width: '45px',
                          height: '45px',
                          borderRadius: '12px',
                          background: transaction.type === 'income' ? '#10B981' : '#EF4444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '18px',
                          fontWeight: '600'
                        }}>
                          {transaction.type === 'income' ? '+' : '-'}
                        </div>
                        <div>
                          <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#333' }}>
                            {transaction.description}
                          </p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            {transaction.category} • {transaction.date}
                          </p>
                        </div>
                      </div>
                      <div className="transaction-amount" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: transaction.type === 'income' ? '#10B981' : '#EF4444'
                        }}>
                          {transaction.type === 'income' ? '+' : ''}${Math.abs(transaction.amount).toFixed(2)}
                        </span>
                        <button
                          className="delete-btn"
                          onClick={() => setShowDeleteConfirm(transaction)}
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            color: '#999'
                          }}
                          title="Delete transaction"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '20px 0 10px 0',
                      borderTop: '1px solid #F3F4F6',
                      marginTop: '15px'
                    }}>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        Showing {startIndex + 1}-{Math.min(endIndex, totalTransactions)} of {totalTransactions} transactions
                      </div>

                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            background: currentPage === 1 ? '#F9FAFB' : 'white',
                            color: currentPage === 1 ? '#9CA3AF' : '#374151',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Previous
                        </button>

                        <div style={{ display: 'flex', gap: '4px' }}>
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={i}
                                onClick={() => setCurrentPage(pageNum)}
                                style={{
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  background: currentPage === pageNum ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                  color: currentPage === pageNum ? 'white' : '#374151',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  minWidth: '36px',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            background: currentPage === totalPages ? '#F9FAFB' : 'white',
                            color: currentPage === totalPages ? '#9CA3AF' : '#374151',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div className="card" style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '20px',
                padding: '30px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <h2 style={{ margin: '0 0 25px 0', fontSize: '20px', fontWeight: '700', color: '#333' }}>
                  Top Categories
                </h2>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {categories.map((category, index) => (
                    <div key={index}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                          {category.name}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                          ${category.amount}
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        background: '#F3F4F6',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${category.percentage}%`,
                          height: '100%',
                          background: category.color,
                          borderRadius: '4px',
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{
                  marginTop: '25px',
                  padding: '20px',
                  background: '#F9FAFB',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                    Monthly Budget
                  </h3>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#8B5CF6', marginBottom: '5px' }}>
                    ${autoBudget.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    ${remaining.toFixed(2)} remaining
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    background: '#E5E7EB',
                    borderRadius: '3px',
                    marginTop: '10px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${percentageUsed}%`,
                      height: '100%',
                      background: '#8B5CF6',
                      borderRadius: '3px',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                </div>

              </div>
            </div>

            <style>
              {`
            @media (max-width: 768px) {
              .logout-btn-mobile {
                display: flex !important;
              }

              .button-group {
                display: flex;
                flex-direction: column;
                width: 100%;
              }

              .mobile-controls {
                flex-direction: column;
              }

              .mobile-controls select,
              .mobile-controls button {
                width: 100%;
              }
            }
          `}
            </style>
          </div>
        </>
      )}
    </>
  );
};

export default Dashboard;